import { PersonClient, PersonDto, PersonPhysicalFeatureDto, PersonPictureDto } from "@app/api2/api";
import { Field, FieldInputProps, FormikProps, useFormikContext } from "formik";
import { Grid, GridColumn, GridRow, Header, Segment, FormGroup as SFormGroup } from "@/components/bp/bp-ui";
import SemanticFormikSelect from "@common/Forms/SemanticFormikSelect";
import { keyOfCheckerNested } from "@common/Utils";
import SemanticFormikTextArea from "@common/Forms/SemanticFormikTextArea";
import { ImageMultiUpload } from "../../../ImageMultiUpload";
import WeightWithUnitField from "@common/Forms/WeightWithUnitField";
import HeightWithUnitField from "@common/Forms/HeightWithUnitField";
import { SectionWithInfo } from "../components/SectionWithInfo";
import { EnrollmentFormSection, EnrollmentFormSectionWithRepeater } from "../components/EnrollmentFormSection";
import { useEyeColorOptions, useFeatureTypeOptions, useHairColorOptions } from "../../Hooks/LookupOptions";


const fn = keyOfCheckerNested<PersonDto>();
const client = new PersonClient();

export const PhysicalTab = () => {
    const { values } = useFormikContext<PersonDto>();

    const eyeColorOptions = useEyeColorOptions({ defaultValue: values.eyeColorId });
    const hairColorOptions = useHairColorOptions({ defaultValue: values.hairColorId });


    return (
        <SectionWithInfo title="Physical Information" info="Enter the individual's physical characteristics, including any distinguishing features. You can also upload up to three photos, such as standard mugshots.">
            <EnrollmentFormSection sectionKey="physical_characteristics" header="Physical Characteristics" >
                {({ editMode }) => (<>
                    <SFormGroup widths="equal">
                        <SemanticFormikSelect
                            disabled={!editMode}
                            name={fn("eyeColorId")}
                            label="Eye Color"
                            placeholder="Select Eye Color"
                            {...eyeColorOptions.getDropdownProps()}
                        />
                        <SemanticFormikSelect
                            disabled={!editMode}
                            name={fn("hairColorId")}
                            label="Hair Color"
                            placeholder="Select Hair Color"
                            {...hairColorOptions.getDropdownProps()}
                        />
                    </SFormGroup>
                    <Grid>
                        <GridRow columns={2}>
                            <GridColumn>
                                <Segment>
                                    <Header as="h4">Height</Header>
                                    <HeightWithUnitField
                                        disabled={!editMode}
                                        nameM={fn("heightInM")}
                                        label="Height"
                                        initialUnit="ft"
                                    />
                                </Segment>
                            </GridColumn>
                            <GridColumn>
                                <Segment>
                                    <Header as="h4">Weight</Header>
                                    <WeightWithUnitField
                                        disabled={!editMode}
                                        nameKg={fn("weightInKg")}
                                        label="Weight"
                                        initialUnit="lbs"
                                    />

                                </Segment>
                            </GridColumn>
                        </GridRow>
                    </Grid>
                </>
                )}
            </EnrollmentFormSection>

            <EnrollmentFormSection sectionKey="pictures" header="Picture(s)" >
                {({ editMode }) => (
                    <Field name={fn("pictures")}>
                        {({ field, form }: { field: FieldInputProps<PersonPictureDto[]>; form: FormikProps<PersonDto>; }) => (
                            <ImageMultiUpload
                                disabled={!editMode}
                                error={form.errors.pictures && form.touched.pictures ? form.errors.pictures as string : ''}
                                name={field.name}
                                imagesUploaded={(name, data) => {
                                    // console.log(name, data);
                                    const transformedData = data.map((item) => ({
                                        id: item.id,
                                        fileName: item.fileName,
                                        contentType: item.mimeType,
                                        content: item.base64DataUrl.split(',')[1]
                                    })) as PersonPictureDto[];
                                    form.handleChange({ target: { name, value: transformedData } });
                                }}
                                value={form.values.pictures.map((item) => ({ id: item.id, fileName: item.fileName, mimeType: item.contentType, base64DataUrl: `data:${item.contentType};base64,${item.content}`, file: null, size: 0 }))}
                            />
                        )}
                    </Field>
                )}
            </EnrollmentFormSection>
            <EnrollmentFormSectionWithRepeater
                sectionKey="physical_features" header="Physical Features"
                instanceType={values}
                name="physicalFeatures"
                newItemFactory={() => ({ id: null, featureTypeId: null, description: '' } as PersonPhysicalFeatureDto)}
                addButtonLabel="Add Physical Feature"
            >
                {({ editMode, idx, item }) => (
                    <PhysicalFeatureRow idx={idx} editMode={editMode} row={item} />
                )}
            </EnrollmentFormSectionWithRepeater>
        </SectionWithInfo>
    );
};


const PhysicalFeatureRow = ({ idx, editMode, row }: { idx: number, editMode: boolean, row: PersonPhysicalFeatureDto }) => {
    const featureTypeOptions = useFeatureTypeOptions({ defaultValue: row.featureTypeId });
    return <Grid>
        <GridRow columns={1}>
            <GridColumn>
                <SemanticFormikSelect
                    name={fn(`physicalFeatures.${idx}.featureTypeId`)}
                    required
                    label="Feature Type"
                    placeholder="Select Feature"
                    disabled={!editMode}
                    {...featureTypeOptions.getDropdownProps()} />
            </GridColumn>
        </GridRow>
        <GridRow columns={1}>
            <GridColumn>
                <SemanticFormikTextArea
                    name={fn(`physicalFeatures.${idx}.description`)}
                    label="Description"
                    disabled={!editMode}
                    maxLength={4000} />
            </GridColumn>
        </GridRow>
    </Grid>;
}

